import { useState, useRef, useEffect } from 'react';
import { User, LogOut } from 'lucide-react';
import useAuth from '../../hooks/useAuth';

/**
 * UserMenu — avatar button with dropdown for authenticated users.
 *
 * When not authenticated, renders a plain "Anmelden" link instead.
 * Replaces the static User icon in DashboardHeader.
 */
const UserMenu = () => {
  const { user, isAuthenticated, logout, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (loading) {
    return (
      <div className="p-2 text-gray-400 rounded-lg">
        <User size={24} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <a
        href="/login"
        className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
        aria-label="Anmelden"
      >
        <User size={24} />
      </a>
    );
  }

  const initials = user.email ? user.email[0].toUpperCase() : '?';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Profil"
        aria-expanded={open}
        className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-medium flex items-center justify-center">
          {initials}
        </div>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs text-gray-500">Angemeldet als</p>
            <p className="text-sm text-gray-900 truncate">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              setOpen(false);
              await logout();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <LogOut size={16} className="text-gray-500" />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
